import { useCallback, useRef } from 'react';
import type { Config, DriveStep, Driver, DriverHook } from 'driver.js';

import { useCalculatorStore } from '../../store/calculatorStore';
import {
  buildCompareContinuationSteps,
  buildInitialTutorialSteps,
  TOUR_AMORTIZATION_TABLE_SELECTOR,
  TOUR_COMPARISON_WINNER_SELECTOR,
  TOUR_EXPAND_AMORT_FIRST_YEAR_EVENT,
  TOUR_SCENARIO_TAB_ACTIONS_SELECTOR,
} from './TutorialDriver';

const TOUR_STORAGE_KEY = 'tour_completed';

/** Compare UI is lazy loaded; polling covers first-time chunk fetch after `setActiveTabId('compare')`. */
const COMPARE_TOUR_HANDOFF_SELECTOR = '[data-tour="tour-comparison-nav"]';
const COMPARE_TOUR_ATTACH_POLL_INTERVAL_MS = 50;
const COMPARE_TOUR_ATTACH_MAX_ATTEMPTS = 240;

/** While set on `<html>`, scenario tab pencil/X stay opaque during the matching Driver step (see tutorial-overrides.css). */
const TOUR_FORCE_TAB_ACTIONS_VISIBLE_CLASS = 'tour-force-scenario-tab-actions-visible';

function isTourComparisonWinnerStep(element: Element | undefined, step: DriveStep): boolean {
  if (element instanceof HTMLElement && element.getAttribute('data-tour') === 'tour-comparison-winner') {
    return true;
  }
  return step.element === TOUR_COMPARISON_WINNER_SELECTOR;
}

/** Compare step 14: driver.js centers the target afterward; defer scroll to top until after that. */
function scrollPageToTopAfterDriverLayout(): void {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
    });
  });
}

type DriverFactory = (options?: Config) => Driver;

let driverFactoryPromise: Promise<DriverFactory> | null = null;

function loadDriverFactory(): Promise<DriverFactory> {
  if (!driverFactoryPromise) {
    driverFactoryPromise = (async () => {
      const [, mod] = await Promise.all([import('driver.js/dist/driver.css'), import('driver.js')]);
      return mod.driver;
    })();
  }
  return driverFactoryPromise;
}

export function hasTourBeenSeen(): boolean {
  try {
    return Boolean(localStorage.getItem(TOUR_STORAGE_KEY));
  } catch {
    return true;
  }
}

export function useTutorial() {
  const driverRef = useRef<Driver | null>(null);
  const skipMarkOnNextDestroyRef = useRef(false);

  const destroyTutorial = useCallback(() => {
    document.documentElement.classList.remove(TOUR_FORCE_TAB_ACTIONS_VISIBLE_CLASS);
    const instance = driverRef.current;
    driverRef.current = null;
    instance?.destroy();
  }, []);

  const markTourCompleted = useCallback(() => {
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, '1');
    } catch {
      /* noop */
    }
  }, []);

  const attachDriver = useCallback(
    (createDriver: DriverFactory, steps: DriveStep[], leg: 'initial' | 'compare' = 'initial') => {
      const config: Config = {
        animate: true,
        showProgress: true,
        progressText: 'Step {{current}} of {{total}}',
        allowClose: true,
        overlayColor: '#0f1f38',
        overlayOpacity: 0.75,
        /* Smooth scroll runs after the first popover layout pass; instant scroll keeps highlight + tip aligned. */
        smoothScroll: false,
        onHighlightStarted: (element, step) => {
          if (leg === 'initial' && step.element === TOUR_AMORTIZATION_TABLE_SELECTOR) {
            window.dispatchEvent(new CustomEvent(TOUR_EXPAND_AMORT_FIRST_YEAR_EVENT));
          }
          if (leg === 'compare' && isTourComparisonWinnerStep(element, step)) {
            scrollPageToTopAfterDriverLayout();
          }
          if (step.element === TOUR_SCENARIO_TAB_ACTIONS_SELECTOR) {
            document.documentElement.classList.add(TOUR_FORCE_TAB_ACTIONS_VISIBLE_CLASS);
          } else {
            document.documentElement.classList.remove(TOUR_FORCE_TAB_ACTIONS_VISIBLE_CLASS);
          }
        },
        onHighlighted: (element, _step, { driver: d }) => {
          if (!(element instanceof HTMLElement)) return;
          if (element.id === 'driver-dummy-element') return;

          if (leg === 'compare' && element.getAttribute('data-tour') === 'tour-comparison-winner') {
            window.scrollTo({ top: 0, behavior: 'auto' });
          }

          if (leg === 'compare' && element.getAttribute('data-tour') === 'tour-comparison-nav') {
            element.scrollIntoView({ block: 'start', behavior: 'auto', inline: 'nearest' });
          }

          /* Reposition after scroll + next paint so popover side/fallback matches final geometry (e.g. step 8). */
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => d.refresh());
          });
        },
        onDestroyStarted: () => {
          destroyTutorial();
        },
        onDestroyed: () => {
          document.documentElement.classList.remove(TOUR_FORCE_TAB_ACTIONS_VISIBLE_CLASS);
          if (!skipMarkOnNextDestroyRef.current) {
            markTourCompleted();
          }
          skipMarkOnNextDestroyRef.current = false;
        },
        steps,
      };

      const instance = createDriver(config);
      driverRef.current = instance;
      return instance;
    },
    [destroyTutorial, markTourCompleted],
  );

  const startTutorial = useCallback(() => {
    if (window.matchMedia('(max-width: 1023px)').matches) {
      console.warn(
        '[MortgageCompareCalculator tutorial] Tour layout is optimized for viewports 1024px and wider; positioning may be off on smaller screens.',
      );
    }

    window.scrollTo({ top: 0, behavior: 'auto' });

    destroyTutorial();

    void import('../calculator/ComparisonView').catch(() => {
      /* Prefetch-only; CalculatorTabs owns the lazy boundary. Swallow failures (eg. offline). */
    });

    const { calculatorTabs, setActiveTabId } = useCalculatorStore.getState();
    const firstScenarioId = calculatorTabs[0]?.id;
    if (firstScenarioId) {
      setActiveTabId(firstScenarioId);
    }

    void (async () => {
      const createDriver = await loadDriverFactory();

      const step10OnNext: DriverHook = (_el, _step, { driver: d }) => {
        const state = useCalculatorStore.getState();
        if (state.calculatorTabs.length < 2) {
          d.moveNext();
          return;
        }

        skipMarkOnNextDestroyRef.current = true;
        driverRef.current = null;
        d.destroy();

        state.setActiveTabId('compare');
        window.scrollTo({ top: 0, behavior: 'auto' });

        const attachCompareTourLeg = () => {
          const next = attachDriver(createDriver, buildCompareContinuationSteps(), 'compare');
          next.drive(0);
        };

        let attempts = 0;
        const pollForCompareTourAnchor = () => {
          if (document.querySelector(COMPARE_TOUR_HANDOFF_SELECTOR)) {
            attachCompareTourLeg();
            return;
          }
          attempts += 1;
          if (attempts >= COMPARE_TOUR_ATTACH_MAX_ATTEMPTS) {
            skipMarkOnNextDestroyRef.current = false;
            console.warn(
              '[MortgageCompareCalculator tutorial] Compare tour handoff timed out waiting for the comparison UI.',
            );
            return;
          }
          window.setTimeout(pollForCompareTourAnchor, COMPARE_TOUR_ATTACH_POLL_INTERVAL_MS);
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            pollForCompareTourAnchor();
          });
        });
      };

      const runFirstLeg = () => {
        const inst = attachDriver(createDriver, buildInitialTutorialSteps(step10OnNext));
        inst.drive(0);
      };

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          runFirstLeg();
        });
      });
    })();
  }, [attachDriver, destroyTutorial]);

  return { startTutorial, destroyTutorial };
}
